/**
 * Single axios instance compartida por todas las features.
 *
 * Es la versión refactorizada de `lib/api-client.ts` · vive aquí porque
 * `shared/api/` es el lugar canónico para la capa HTTP cross-feature.
 * El archivo viejo en `lib/api-client.ts` re-exporta este `axiosClient`
 * con el alias `apiClient` para no romper los call sites que aún no
 * migran a la nueva estructura.
 */
import axios, {
  AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import { useAuthStore } from "@/lib/auth-store";
import type { AuthResponse, RefreshRequest } from "@/lib/types";

// En Vite las env vars van por `import.meta.env.VITE_*` · si no está
// definida usamos baseURL vacía para que las requests vayan a paths
// relativos y el dev-server las proxie a `/api` → backend (configurado
// en `vite.config.ts`). En producción nginx sirve el SPA y proxia
// `/api/*` al backend .NET con el mismo path.
const baseURL =
  (import.meta.env.VITE_API_URL as string | undefined) ?? "";

export const axiosClient = axios.create({
  baseURL,
  headers: { "Content-Type": "application/json" },
});

/**
 * Versionado del API (P32) · el SPA llama SIEMPRE a /api/v1. Los servicios
 * siguen escribiendo "/api/..." (sin versión) y este interceptor central
 * antepone la versión · subir a v2 en el futuro es cambiar UNA constante.
 * Los healthchecks (/api/health*) son infraestructura sin versión.
 */
const API_VERSION = "v1";

function versionarUrl(url: string): string {
  if (!/^\/api\//.test(url)) return url; // no es del API
  if (/^\/api\/v\d+\//.test(url)) return url; // ya versionada
  if (/^\/api\/health(\/|$)/.test(url)) return url; // infra sin versión
  return url.replace(/^\/api\//, `/api/${API_VERSION}/`);
}

axiosClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (config.url) {
      config.url = versionarUrl(config.url);
    }
    const token = useAuthStore.getState().accessToken;
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  }
);

// Single-flight refresh: si varios requests reciben 401 al mismo tiempo
// solo el primero llama a /auth/refresh y los demás esperan al resultado.
let refreshPromise: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  const refreshToken = useAuthStore.getState().refreshToken;
  if (!refreshToken) return null;
  try {
    const body: RefreshRequest = { refreshToken };
    const { data } = await axios.post<AuthResponse>(
      `${baseURL}/api/v1/auth/refresh`,
      body,
      { headers: { "Content-Type": "application/json" } }
    );
    useAuthStore.getState().setSession(data);
    return data.accessToken;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

axiosClient.interceptors.response.use(
  (resp) => resp,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retry &&
      // El url ya viene versionado por el interceptor (/api/v1/auth/...) ·
      // el regex acepta con o sin versión para no depender del orden.
      !/\/api\/(v\d+\/)?auth\//.test(original.url ?? "")
    ) {
      original._retry = true;
      refreshPromise ??= performRefresh().finally(() => {
        refreshPromise = null;
      });
      const newToken = await refreshPromise;
      if (newToken && original.headers) {
        original.headers.Authorization = `Bearer ${newToken}`;
        return axiosClient(original);
      }
    }
    return Promise.reject(error);
  }
);
